/**
 * Password protection: an open password, restrictions, or both.
 *
 * Takes effect on Save -- the document being edited is not encrypted, only
 * the file written. For a restricted file opened without its permissions
 * password, the dialog first asks for that password, as Acrobat does.
 */

import { useEffect, useState } from 'react'
import { api, type Protection, type SessionInfo } from '../api'

interface Props {
  sessionId: string
  onClose: () => void
  onChanged: (session: SessionInfo, message: string) => void
}

/** The permissions shown, in the order a person thinks about them. */
const SHOWN = [
  'print_highres', 'extract', 'modify_other', 'modify_assembly',
  'modify_annotation', 'modify_form',
]

export function ProtectDialog({ sessionId, onClose, onChanged }: Props) {
  const [state, setState] = useState<Protection | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const [needOpen, setNeedOpen] = useState(false)
  const [openPassword, setOpenPassword] = useState('')
  const [openConfirm, setOpenConfirm] = useState('')
  const [restrict, setRestrict] = useState(false)
  const [allow, setAllow] = useState<Record<string, boolean>>({})
  const [permPassword, setPermPassword] = useState('')
  const [unlockPassword, setUnlockPassword] = useState('')

  useEffect(() => {
    void api.security(sessionId).then((s) => {
      setState(s)
      setRestrict(s.restricted)
      setAllow(s.allow)
      setNeedOpen(s.openPassword)
    }).catch((err) => setError(err.message))
  }, [sessionId])

  const act = async (work: () => Promise<{ session: SessionInfo; security: Protection }>, message: string) => {
    setBusy(true)
    setError('')
    try {
      const result = await work()
      setState(result.security)
      onChanged(result.session, message)
      if (!result.security.locked && message) onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (!state) {
    return (
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal__panel"><div className="modal__body">Loading&hellip;</div></div>
      </div>
    )
  }

  if (state.locked) {
    return (
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="protect-title">
        <form className="modal__panel" onSubmit={(e) => {
          e.preventDefault()
          void act(() => api.unlock(sessionId, unlockPassword), '')
        }}>
          <h2 className="modal__title" id="protect-title">Restricted document</h2>
          <div className="modal__body">
            <p className="modal__hint">
              This file&rsquo;s permissions limit what can be changed. Enter its
              permissions password to lift the restrictions or change its protection.
            </p>
            <ul className="perm-list">
              {SHOWN.map((key) => (
                <li key={key} data-allowed={state.allow[key]}>
                  {state.allow[key] ? '✓' : '✕'} {state.permissions[key]}
                </li>
              ))}
            </ul>
            <label className="field">
              <span>Permissions password</span>
              <input type="password" autoComplete="off" value={unlockPassword}
                     onChange={(e) => setUnlockPassword(e.target.value)} />
            </label>
            {error && <p className="modal__hint modal__hint--warn">{error}</p>}
          </div>
          <div className="modal__footer">
            <button type="button" className="btn" onClick={onClose}>Close</button>
            <button type="submit" className="btn btn--primary" disabled={!unlockPassword || busy}>Unlock</button>
          </div>
        </form>
      </div>
    )
  }

  const mismatch = needOpen && openPassword !== openConfirm
  const changingOpen = needOpen && (openPassword || !state.openPassword)
  const apply = () => {
    if (needOpen && state.openPassword && !openPassword) {
      // Keeping the existing open password requires knowing it; ask for it again.
      setError('Enter the open password again to keep it, or a new one.')
      return
    }
    void act(() => api.setSecurity(sessionId, {
      openPassword: needOpen ? openPassword : '',
      permissionsPassword: restrict ? permPassword : '',
      allow: restrict ? allow : {},
    }), 'Protection will be applied when you save.')
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="protect-title">
      <div className="modal__panel">
        <h2 className="modal__title" id="protect-title">Protect</h2>
        <div className="modal__body">
          <label className="check">
            <input type="checkbox" checked={needOpen} onChange={(e) => setNeedOpen(e.target.checked)} />
            Require a password to open
          </label>
          {needOpen && (
            <div className="field-row">
              <label className="field">
                <span>Open password</span>
                <input type="password" autoComplete="new-password" value={openPassword}
                       placeholder={state.openPassword ? 'unchanged' : ''}
                       onChange={(e) => setOpenPassword(e.target.value)} />
              </label>
              <label className="field">
                <span>Confirm</span>
                <input type="password" autoComplete="new-password" value={openConfirm}
                       onChange={(e) => setOpenConfirm(e.target.value)} />
              </label>
            </div>
          )}

          <label className="check">
            <input type="checkbox" checked={restrict} onChange={(e) => setRestrict(e.target.checked)} />
            Restrict what others can do
          </label>
          {restrict && (
            <>
              <div className="perm-grid">
                {SHOWN.map((key) => (
                  <label className="check" key={key}>
                    <input type="checkbox" checked={allow[key] ?? true}
                           onChange={(e) => setAllow({ ...allow, [key]: e.target.checked,
                             ...(key === 'print_highres' ? { print_lowres: e.target.checked } : {}) })} />
                    Allow: {state.permissions[key]}
                  </label>
                ))}
              </div>
              <label className="field">
                <span>Permissions password &mdash; needed to change these later</span>
                <input type="password" autoComplete="new-password" value={permPassword}
                       onChange={(e) => setPermPassword(e.target.value)} />
              </label>
            </>
          )}

          <p className="modal__hint">
            Applied when you save, as AES-256 &mdash; the document you are editing
            stays open as it is. Keep the passwords somewhere safe: without the
            open password, nobody can open the saved file, including you.
          </p>
          {mismatch && changingOpen && <p className="modal__hint modal__hint--warn">The two open passwords do not match.</p>}
          {error && <p className="modal__hint modal__hint--warn">{error}</p>}
        </div>
        <div className="modal__footer">
          {state.protected && (
            <button className="btn btn--danger" disabled={busy} style={{ marginRight: 'auto' }}
                    onClick={() => void act(() => api.setSecurity(sessionId, { remove: true }),
                      'Protection will be removed when you save.')}>
              Remove protection
            </button>
          )}
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={apply}
                  disabled={busy || (mismatch && Boolean(changingOpen)) || (!needOpen && !restrict)
                    || (restrict && !permPassword)}>
            Apply
          </button>
        </div>
      </div>
    </div>
  )
}
